import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";

const VARIANTS = {
  primary:
    "bg-[#1A1C1E] text-[#F7F5F0] border border-[#1A1C1E] hover:bg-[#2D3B32] hover:border-[#2D3B32]",
  secondary:
    "bg-transparent text-[#1A1C1E] border border-[#C8C2B7] hover:border-[#1A1C1E] hover:bg-[#EFECE6]",
  light:
    "bg-[#F7F5F0] text-[#1A1C1E] border border-[#F7F5F0] hover:bg-transparent hover:text-[#F7F5F0]",
  ghost:
    "bg-transparent text-[#1A1C1E] border border-transparent hover:border-[#C8C2B7]",
};

/**
 * SSButton — editorial sharp-edged action with a subtle desktop magnetic pull.
 * Renders as a router Link (`to`), an anchor (`href`) or a button.
 */
export const SSButton = ({
  children,
  variant = "primary",
  to,
  href,
  onClick,
  type = "button",
  arrow = true,
  className,
  magnetic = true,
  ...rest
}) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });

  const onMove = (e) => {
    if (reduce || !magnetic || !ref.current) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = ref.current.getBoundingClientRect();
    const mx = e.clientX - (r.left + r.width / 2);
    const my = e.clientY - (r.top + r.height / 2);
    x.set(mx * 0.18);
    y.set(my * 0.3);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const classes = cn(
    "group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[0.8rem] font-medium tracking-[0.08em] uppercase font-body transition-colors duration-300 rounded-none",
    VARIANTS[variant],
    className
  );

  const inner = (
    <>
      <span>{children}</span>
      {arrow && (
        <ArrowUpRight
          className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          strokeWidth={1.5}
        />
      )}
    </>
  );

  const motionProps = {
    ref,
    style: { x: sx, y: sy },
    onMouseMove: onMove,
    onMouseLeave: onLeave,
    className: classes,
  };

  if (to) {
    return (
      <motion.div {...motionProps} style={{ ...motionProps.style, display: "inline-flex" }}>
        <Link to={to} onClick={onClick} className="inline-flex items-center gap-2" {...rest}>
          {inner}
        </Link>
      </motion.div>
    );
  }
  if (href) {
    return (
      <motion.a
        {...motionProps}
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        onClick={onClick}
        {...rest}
      >
        {inner}
      </motion.a>
    );
  }
  return (
    <motion.button {...motionProps} type={type} onClick={onClick} {...rest}>
      {inner}
    </motion.button>
  );
};

export default SSButton;
